/**
 * @file PlanYearFilesTab.js
 * @description Tab component for managing plan year-related files.
 */

import { useState, useEffect, useMemo } from 'react';
import PlanFilesManager from './PlanFilesManager';
import { fetchPopulationGroups } from '../../../services/populationGroupService';
import VideoThumbnail from '../../ui/VideoThumbnail';
import { getFileIcon } from '../../ui/svg/FileIcons';
import { getFileCategory } from '../../helpers/FileUtils';
import styles from '../PlanYear.module.scss';
import { getText } from '../../../i18n';

const MAX_VISIBLE_GROUPS = 2;

const PlanYearFilesTab = ({ data, onChange, onSave }) => {
    const [popGroupMap, setPopGroupMap] = useState({});
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [activeActionMenuId, setActiveActionMenuId] = useState(null);

    const toggleExpanded = assetId =>
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(assetId)) next.delete(assetId);
            else next.add(assetId);
            return next;
        });

    useEffect(() => {
        const loadPopulationGroups = async () => {
            try {
                const groups = await fetchPopulationGroups(null);
                const groupMap = {};
                groups.forEach(group => {
                    groupMap[group.id] = group.name;
                });
                setPopGroupMap(groupMap);
            } catch (error) {
                console.error('Failed to fetch population groups', error);
            }
        };

        loadPopulationGroups();
    }, []);

    useEffect(() => {
        const handleClick = () => {
            setActiveActionMenuId(null);
        };

        document.addEventListener('click', handleClick);
        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, []);

    const isVideoFile = fileType => getFileCategory(fileType) === 'video';
    const isImageFile = fileType => getFileCategory(fileType) === 'image';

    const planYearAssetMap = useMemo(() => {
        const attachmentMap = {};
        const attachments = data?.planYearAssets || [];

        attachments.forEach(attachment => {
            const assetId = attachment?.assetId;
            if (!assetId) {
                return;
            }

            attachmentMap[assetId] = attachment;
        });

        return attachmentMap;
    }, [data?.planYearAssets]);

    const getGroupNames = assetFile => {
        const assetId = assetFile?.id || assetFile?.assetId;
        if (!assetId) {
            return [];
        }

        const attachment = planYearAssetMap[assetId] || {};
        const groupsFromAttachment = Array.isArray(attachment.populationGroups)
            ? attachment.populationGroups
            : [];

        if (groupsFromAttachment.length > 0) {
            return groupsFromAttachment
                .map(group => group?.name)
                .filter(Boolean);
        }

        const groupIds = Array.isArray(attachment.populationGroupIds)
            ? attachment.populationGroupIds
            : [];

        return groupIds
            .map(
                groupId =>
                    popGroupMap[groupId] || popGroupMap[+groupId] || null,
            )
            .filter(Boolean);
    };

    const renderFileThumbnail = assetFile => {
        if (isVideoFile(assetFile.fileType)) {
            return (
                <VideoThumbnail
                    videoUrl={assetFile.url}
                    avatarURL={assetFile.avatarURL}
                    width={80}
                    height={64}
                    alt={assetFile.name}
                />
            );
        }

        if (isImageFile(assetFile.fileType) && assetFile.url) {
            return <img src={assetFile.url} alt={assetFile.name} />;
        }

        return getFileIcon(assetFile.fileType);
    };

    const renderFileRow = (
        assetFile,
        index,
        { onDelete, onPreview, onEdit, onDownload, canPreview },
    ) => {
        const groupNames = getGroupNames(assetFile);
        const isExpanded = expandedRows.has(assetFile.id);
        const hiddenCount = Math.max(groupNames.length - MAX_VISIBLE_GROUPS, 0);
        const isActionMenuOpen = activeActionMenuId === assetFile.id;

        return (
            <div key={assetFile.id} className={styles.fileBox}>
                <div className={styles.fileThumbnail}>
                    {renderFileThumbnail(assetFile)}
                </div>

                <div className={styles.fileInfo}>
                    <div className={styles.fileName}>{assetFile.name}</div>
                    <div className={styles.fileType}>
                        {assetFile.fileType?.toUpperCase()}
                    </div>
                    <div className={styles.sharedToHeader}>
                        <div className={styles.sharedToLabel}>{getText('msg_de5997454542')}</div>
                        {hiddenCount > 0 && groupNames.length > 0 && (
                            <span
                                className={styles.moreBadge}
                                role="button"
                                tabIndex={0}
                                onClick={() => toggleExpanded(assetFile.id)}
                                onKeyDown={event => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault();
                                        toggleExpanded(assetFile.id);
                                    }
                                }}
                            >
                                {isExpanded
                                    ? 'Show less'
                                    : `+ ${hiddenCount} more`}
                            </span>
                        )}
                    </div>
                    {groupNames.length > 0 && (
                        <div
                            className={`${styles.sharedToBadges} ${
                                isExpanded ? styles.sharedToBadgesExpanded : ''
                            }`}
                        >
                            {groupNames.map((name, i) => {
                                const shouldRender =
                                    isExpanded || i < MAX_VISIBLE_GROUPS;

                                if (!shouldRender) return null;

                                return (
                                    <span key={i} className={styles.groupBadge}>
                                        {name}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className={styles.fileActions}>
                    <button
                        type="button"
                        className={styles.actionIconDelete}
                        onClick={() => onDelete(assetFile)}
                        title={getText('msg_b9ea4b3f01ac')}
                    >
                        <i className="bi bi-trash3"></i>
                    </button>

                    <div className={styles.actionMenuWrapper}>
                        <button
                            type="button"
                            className={styles.actionIconPreview}
                            onClick={event => {
                                event.stopPropagation();
                                setActiveActionMenuId(prev =>
                                    prev === assetFile.id ? null : assetFile.id,
                                );
                            }}
                            title={getText('msg_9e1b94c56613')}
                        >
                            <i className="bi bi-eye"></i>
                        </button>
                        {isActionMenuOpen && (
                            <div
                                className={styles.actionMenu}
                                onClick={event => event.stopPropagation()}
                            >
                                <ul>
                                    {canPreview(assetFile) && (
                                        <li
                                            onClick={() => {
                                                onPreview(index);
                                                setActiveActionMenuId(null);
                                            }}
                                        >
                                            {getText('msg_f1fbb2b43dca')}
                                        </li>
                                    )}
                                    <li
                                        onClick={() => {
                                            onDownload(assetFile);
                                            setActiveActionMenuId(null);
                                        }}
                                    >
                                        {getText('msg_a479c9c34e87')}
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        className={styles.actionIconEdit}
                        onClick={() => onEdit(assetFile)}
                        title={getText('msg_43b9013be021')}
                    >
                        <i className="bi bi-pencil"></i>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <PlanFilesManager
            data={data}
            onChange={onChange}
            storageKey="planYearAssets"
            entityLabel={getText('msg_7edff1515fcc')}
            headerTitle={getText('msg_3ca993ac7de8')}
            headerDescription={getText('msg_446876794d97')}
            logContext={getText('msg_39e6e4d6cf1d')}
            renderFileRow={renderFileRow}
            showPopulationGroups={true}
            persistPopulationGroupsOnAsset={false}
        />
    );
};

export default PlanYearFilesTab;
